import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, IconButton, Button,
  TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert, Snackbar,
  Tooltip, Divider, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Autocomplete, FormControlLabel, Switch, Tabs, Tab,
  Menu, ListItemIcon, ListItemText,
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon, Edit as EditIcon, Delete as DeleteIcon,
  Close as CloseIcon, Refresh as RefreshIcon, Print as PrintIcon,
  Description as OrderIcon, Person as PersonIcon,
  Cancel as CancelIcon, ContentCopy as DuplicateIcon, MoreVert as MoreIcon,
  MedicalServices as MedicalIcon, Visibility as ViewIcon,
  RemoveCircleOutline as RemoveIcon, AddCircleOutline as AddItemIcon,
  Science as ScienceIcon, Warning as WarningIcon,
  CheckCircle as CheckIcon, Schedule as ScheduleIcon,
  LocalHospital as HospitalIcon, Biotech as BiotechIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import { CLINIC_DOC_SIGNATURE_CSS } from '../../utils/clinicPrintLetterhead';
import { useClinicPageBreakpoints, clinicPageContainerSx } from '../../hooks/useClinicPageBreakpoints';


interface TabPanelProps { children?: React.ReactNode; index: number; value: number; }
function TabPanel({ children, value, index }: TabPanelProps) {
  return <div hidden={value !== index}>{value === index && <Box sx={{ pt: 2 }}>{children}</Box>}</div>;
}

const typeLabels: Record<string, string> = {
  standard: 'Padrão', urgent: 'Urgente', emergency: 'Emergência', routine: 'Rotina',
  pre_operative: 'Pré-Operatório', post_operative: 'Pós-Operatório', follow_up: 'Acompanhamento',
};
const urgencyLabels: Record<string, string> = { routine: 'Rotina', urgent: 'Urgente', emergency: 'Emergência' };
const urgencyColors: Record<string, string> = { routine: '#14b8a6', urgent: '#f59e0b', emergency: '#ef4444' };
const statusLabels: Record<string, string> = {
  pending: 'Pendente', sent: 'Enviado', in_progress: 'Em Andamento',
  partially_received: 'Parcial', completed: 'Concluído', cancelled: 'Cancelado',
};
const statusColors: Record<string, string> = {
  pending: '#6b7280', sent: '#3b82f6', in_progress: '#f59e0b',
  partially_received: '#8b5cf6', completed: '#10b981', cancelled: '#ef4444',
};

const lateralityOptions = ['Não se aplica', 'Bilateral', 'Esquerdo', 'Direito'];

const emptyItem = {
  exam_catalog_id: '', exam_name: '', exam_code: '', exam_category: '',
  specimen_type: '', body_region: '', laterality: 'Não se aplica',
  preparation_instructions: '', collection_instructions: '', clinical_justification: '',
  urgency: 'routine', sort_order: 0,   notes: '', price: '', commission_percentage: '',
};

const formatDate = (d: string) => { if (!d) return '-'; const dt = new Date(d + 'T12:00:00'); return dt.toLocaleDateString('pt-BR'); };

const ExamOrders: React.FC = () => {
  const { isMobile } = useClinicPageBreakpoints();

  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [urgencyFilter, setUrgencyFilter] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [stats, setStats] = useState<any>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [viewOrder, setViewOrder] = useState<any>(null);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [printData, setPrintData] = useState<any>(null);
  const printRef = useRef<HTMLDivElement>(null);

  // Form states
  const [catalogDialogOpen, setCatalogDialogOpen] = useState(false);
  const [catalogList, setCatalogList] = useState<any[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogFormOpen, setCatalogFormOpen] = useState(false);
  const [catalogForm, setCatalogForm] = useState({ name: '', code: '', category_id: '', price: '', commission_percentage: '', description: '' });
  const [catalogEditingId, setCatalogEditingId] = useState<string | null>(null);
  const [catalogCategories, setCatalogCategories] = useState<any[]>([]);
  const [patientSearch, setPatientSearch] = useState('');
  const [patientOptions, setPatientOptions] = useState<any[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<any>(null);
  const [professionalSearch, setProfessionalSearch] = useState('');
  const [professionalOptions, setProfessionalOptions] = useState<any[]>([]);
  const [selectedProfessional, setSelectedProfessional] = useState<any>(null);
  const [examSearch, setExamSearch] = useState('');
  const [examOptions, setExamOptions] = useState<any[]>([]);
  const [items, setItems] = useState<any[]>([{ ...emptyItem }]);
  const [orderForm, setOrderForm] = useState({
    order_type: 'standard', urgency: 'routine',
    diagnosis: '', diagnosis_code: '', clinical_indication: '',
    clinical_history: '', current_medications: '', allergies: '',
    destination_lab: '', destination_address: '', destination_phone: '',
    notes: '', internal_notes: '', special_instructions: '',
  });

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (urgencyFilter) params.urgency = urgencyFilter;
      const res = await axios.get(getApiUrl('/clinic/exam-orders'), { params });
      setOrders(res.data?.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(getApiUrl('/clinic/exam-orders/stats'));
      setStats(res.data?.data);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchOrders(); fetchStats(); }, [search, statusFilter, urgencyFilter]);

  // Search patients
  useEffect(() => {
    if (!dialogOpen) return;
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(getApiUrl('/clinic/exam-orders-search/patients'), { params: { search: patientSearch || '' } });
        setPatientOptions(res.data?.data || []);
      } catch (err) { console.error(err); }
    }, patientSearch.length > 0 ? 300 : 100);
    return () => clearTimeout(timer);
  }, [patientSearch, dialogOpen]);

  useEffect(() => { if (dialogOpen) { setPatientSearch(''); setExamSearch(''); } }, [dialogOpen]);

  // Search professionals
  useEffect(() => {
    if (!dialogOpen) return;
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(getApiUrl('/clinic/exam-orders-search/professionals'), { params: { search: professionalSearch || '' } });
        setProfessionalOptions(res.data?.data || []);
      } catch (err) { console.error(err); }
    }, professionalSearch.length > 0 ? 300 : 100);
    return () => clearTimeout(timer);
  }, [professionalSearch, dialogOpen]);

  // Search exams catalog
  useEffect(() => {
    if (!dialogOpen) return;
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(getApiUrl('/clinic/exam-orders-search/exams'), { params: { search: examSearch || '' } });
        setExamOptions(res.data?.data || []);
      } catch (err) { console.error(err); }
    }, examSearch.length > 0 ? 300 : 100);
    return () => clearTimeout(timer);
  }, [examSearch, dialogOpen]);

  const resetForm = () => {
    setSelectedPatient(null); setSelectedProfessional(null);
    setPatientSearch(''); setPatientOptions([]); setProfessionalSearch(''); setProfessionalOptions([]);
    setExamSearch(''); setExamOptions([]);
    setOrderForm({ order_type: 'standard', urgency: 'routine', diagnosis: '', diagnosis_code: '', clinical_indication: '', clinical_history: '', current_medications: '', allergies: '', destination_lab: '', destination_address: '', destination_phone: '', notes: '', internal_notes: '', special_instructions: '' });
    setItems([{ ...emptyItem }]); setEditingId(null); setTabValue(0);
  };

  const addItem = () => setItems([...items, { ...emptyItem, sort_order: items.length }]);
  const removeItem = (idx: number) => { if (items.length > 1) setItems(items.filter((_, i) => i !== idx)); };
  const updateItem = (idx: number, field: string, value: any) => {
    const updated = [...items]; updated[idx] = { ...updated[idx], [field]: value }; setItems(updated);
  };

  const selectExamForItem = (idx: number, exam: any) => {
    if (!exam) return;
    const updated = [...items];
    updated[idx] = {
      ...updated[idx],
      exam_catalog_id: exam.id,
      exam_name: exam.name,
      exam_code: exam.code || '',
      exam_category: exam.category?.name || '',
      specimen_type: exam.specimen_type || '',
      preparation_instructions: exam.preparation_instructions || '',
      collection_instructions: exam.collection_instructions || '',
      price: exam.price != null ? exam.price : '',
      commission_percentage: exam.commission_percentage != null && exam.commission_percentage !== '' ? exam.commission_percentage : '',
    };
    setItems(updated);
  };

  const handleSave = async () => {
    if (!selectedPatient) { setSnackbar({ open: true, message: 'Selecione um paciente', severity: 'error' }); return; }
    if (!orderForm.clinical_indication.trim()) { setSnackbar({ open: true, message: 'Preencha a indicação clínica', severity: 'error' }); return; }
    if (!items.some(i => i.exam_name.trim())) { setSnackbar({ open: true, message: 'Adicione pelo menos um exame', severity: 'error' }); return; }

    setSaving(true);
    try {
      const payload = {
        ...orderForm,
        patient_id: selectedPatient.id,
        professional_id: selectedProfessional?.id || null,
        items: items.filter(i => i.exam_name.trim()).map((item, idx) => ({ ...item, sort_order: idx })),
      };

      if (editingId) {
        await axios.put(getApiUrl(`/clinic/exam-orders/${editingId}`), payload);
        setSnackbar({ open: true, message: 'Pedido de exame atualizado!', severity: 'success' });
      } else {
        await axios.post(getApiUrl('/clinic/exam-orders'), payload);
        setSnackbar({ open: true, message: 'Pedido de exame criado com sucesso!', severity: 'success' });
      }
      setDialogOpen(false); resetForm(); fetchOrders(); fetchStats();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Erro ao salvar pedido', severity: 'error' });
    }
    setSaving(false);
  };

  const handleEdit = async (order: any) => {
    setEditingId(order.id);
    setSelectedPatient({ id: order.patient_id, full_name: order.patient_name, cpf: order.patient_cpf, phone: order.patient_phone });
    if (order.professional_id) {
      setSelectedProfessional({ id: order.professional_id, full_name: order.professional_name, registration_number: order.professional_registration, specialty: order.professional_specialty });
    }
    setOrderForm({
      order_type: order.order_type, urgency: order.urgency, diagnosis: order.diagnosis || '',
      diagnosis_code: order.diagnosis_code || '', clinical_indication: order.clinical_indication || '',
      clinical_history: order.clinical_history || '', current_medications: order.current_medications || '',
      allergies: order.allergies || '', destination_lab: order.destination_lab || '',
      destination_address: order.destination_address || '', destination_phone: order.destination_phone || '',
      notes: order.notes || '', internal_notes: order.internal_notes || '', special_instructions: order.special_instructions || '',
    });
    setItems((order.items || []).length > 0 ? order.items : [{ ...emptyItem }]);
    setTabValue(0); setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este pedido?')) return;
    try {
      await axios.delete(getApiUrl(`/clinic/exam-orders/${id}`));
      setSnackbar({ open: true, message: 'Pedido excluído', severity: 'success' });
      fetchOrders(); fetchStats();
    } catch (err) { setSnackbar({ open: true, message: 'Erro ao excluir', severity: 'error' }); }
  };

  const handleStatusChange = async (id: string, status: string) => {
    try {
      await axios.patch(getApiUrl(`/clinic/exam-orders/${id}/status`), { status });
      setSnackbar({ open: true, message: 'Status atualizado', severity: 'success' });
      fetchOrders(); fetchStats();
    } catch (err) { setSnackbar({ open: true, message: 'Erro ao atualizar status', severity: 'error' }); }
  };

  const handleDuplicate = async (id: string) => {
    try {
      await axios.post(getApiUrl(`/clinic/exam-orders/${id}/duplicate`));
      setSnackbar({ open: true, message: 'Pedido duplicado com sucesso!', severity: 'success' });
      fetchOrders(); fetchStats();
    } catch (err) { setSnackbar({ open: true, message: 'Erro ao duplicar', severity: 'error' }); }
  };

  const handlePrint = async (order: any) => {
    try {
      const res = await axios.get(getApiUrl(`/clinic/exam-orders/${order.id}/print`));
      setPrintData(res.data?.data); setPrintDialogOpen(true);
    } catch (err) { setSnackbar({ open: true, message: 'Erro ao gerar impressão', severity: 'error' }); }
  };

  const handleView = async (order: any) => {
    try {
      const res = await axios.get(getApiUrl(`/clinic/exam-orders/${order.id}`));
      setViewOrder(res.data?.data); setViewDialogOpen(true);
    } catch (err) { console.error(err); }
  };

  const executePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(`<!DOCTYPE html><html><head><title>Pedido de Exames</title><style>
      @page { size: A4; margin: 15mm; }
      body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 12px; color: #333; margin: 0; padding: 0; }
      .print-container { max-width: 700px; margin: 0 auto; padding: 20px; }
      .doc-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 14px; margin-bottom: 15px; padding-bottom: 10px; border-bottom: 3px solid #14b8a6; }
      .doc-head-logo { flex-shrink: 0; }
      .doc-head-logo img { display: block; max-width: 150px; max-height: 56px; width: auto; height: auto; object-fit: contain; object-position: left top; }
      .doc-head-center { flex: 1; min-width: 0; text-align: center; align-self: center; }
      .doc-head-meta { flex-shrink: 0; max-width: 42%; font-size: 10px; color: #6b7280; text-align: right; line-height: 1.45; }
      .doc-head .clinic-name { font-size: 18px; font-weight: 700; color: #0d9488; margin: 0; }
      .clinic-subtitle { font-size: 11px; color: #6b7280; margin-top: 2px; }
      .order-title { text-align: center; font-size: 16px; font-weight: 700; color: #0d9488; margin: 15px 0 5px; text-transform: uppercase; letter-spacing: 1px; }
      .order-number { text-align: center; font-size: 10px; color: #9ca3af; margin-bottom: 15px; }
      .urgency-badge { text-align: center; margin-bottom: 10px; }
      .urgency-badge span { display: inline-block; padding: 3px 14px; border-radius: 12px; font-size: 11px; font-weight: 700; text-transform: uppercase; }
      .section { margin-bottom: 12px; }
      .section-title { font-weight: 700; font-size: 12px; color: #0d9488; margin-bottom: 6px; padding-bottom: 3px; border-bottom: 1px solid #d1fae5; }
      .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px; }
      .info-item { font-size: 11px; line-height: 1.4; }
      .info-label { font-weight: 600; color: #374151; }
      .info-value { color: #4b5563; }
      table { width: 100%; border-collapse: collapse; margin-top: 6px; }
      th { background: #f0fdf4; border: 1px solid #d1fae5; padding: 6px 8px; text-align: left; font-size: 10px; font-weight: 700; color: #0d9488; }
      td { border: 1px solid #e5e7eb; padding: 6px 8px; font-size: 11px; }
      .exam-name { font-weight: 600; }
      .exam-prep { font-size: 9px; color: #6b7280; margin-top: 2px; font-style: italic; }
      .preparation-box { background: #fefce8; border: 1px solid #fde68a; border-radius: 6px; padding: 10px; margin-top: 12px; }
      .preparation-box h4 { margin: 0 0 6px; font-size: 12px; color: #b45309; }
      .preparation-item { font-size: 10px; margin-bottom: 4px; color: #78350f; }
      ${CLINIC_DOC_SIGNATURE_CSS}
      .validity { text-align: left; margin-top: 24px; margin-bottom: 0; font-size: 10px; color: #6b7280; }
      .footer-note { text-align: center; margin-top: 8px; font-size: 9px; color: #9ca3af; }
      .destination-box { background: #f0f9ff; border: 1px solid #bae6fd; border-radius: 6px; padding: 10px; margin-top: 8px; }
    </style></head><body>${content.innerHTML}</body></html>`);
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 500);
  };

  return (
    <Box sx={clinicPageContainerSx}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#111', fontSize: { xs: '1.5rem', md: '2rem' } }}>Pedidos de Exames</Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>Gerencie pedidos de exames laboratoriais, de imagem e complementares</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={() => { fetchOrders(); fetchStats(); }} sx={{ color: '#14b8a6' }}><RefreshIcon /></IconButton>
          <Button variant="outlined" startIcon={<BiotechIcon />} onClick={async () => {
            setCatalogDialogOpen(true);
            setCatalogLoading(true);
            try {
              const [catRes, catListRes] = await Promise.all([
                axios.get(getApiUrl('/clinic/exam-orders/categories')),
                axios.get(getApiUrl('/clinic/exam-orders/catalog')),
              ]);
              setCatalogCategories(catRes.data?.data || []);
              setCatalogList(catListRes.data?.data || []);
            } catch (e) { console.error(e); }
            setCatalogLoading(false);
          }} sx={{ borderColor: '#8b5cf6', color: '#8b5cf6', textTransform: 'none', fontWeight: 600 }}>
            Catálogo de Exames
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { resetForm(); setDialogOpen(true); }}
            sx={{ bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none', fontWeight: 600 }}>
            Novo Pedido
          </Button>
        </Box>
      </Box>

      {/* KPI Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Pendentes', value: stats.total_pending, icon: <ScheduleIcon />, color: '#6b7280' },
            { label: 'Emitidos Hoje', value: stats.total_today, icon: <OrderIcon />, color: '#14b8a6' },
            { label: 'Este Mês', value: stats.total_month, icon: <ScienceIcon />, color: '#3b82f6' },
            { label: 'Urgentes', value: stats.total_urgent, icon: <WarningIcon />, color: '#f59e0b' },
            { label: 'Concluídos', value: stats.total_completed, icon: <CheckIcon />, color: '#10b981' },
          ].map((kpi, idx) => (
            <Grid item xs={6} sm={4} md key={idx}>
              <Card sx={{ borderRadius: 3, border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ p: 1, borderRadius: 2, bgcolor: `${kpi.color}15` }}>
                    {React.cloneElement(kpi.icon, { sx: { color: kpi.color, fontSize: 24 } })}
                  </Box>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#111' }}>{kpi.value}</Typography>
                    <Typography variant="caption" sx={{ color: '#6b7280' }}>{kpi.label}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3, borderRadius: 3, border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField fullWidth size="small" placeholder="Buscar por paciente, profissional ou número..." value={search}
                onChange={(e) => setSearch(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#9ca3af' }} /></InputAdornment> }} />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                  <MenuItem value="">Todos</MenuItem>
                  {Object.entries(statusLabels).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Urgência</InputLabel>
                <Select value={urgencyFilter} label="Urgência" onChange={(e) => setUrgencyFilter(e.target.value)}>
                  <MenuItem value="">Todas</MenuItem>
                  {Object.entries(urgencyLabels).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      <Card sx={{ borderRadius: 3, border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress sx={{ color: '#14b8a6' }} /></Box>
        ) : orders.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <ScienceIcon sx={{ fontSize: 64, color: '#d1d5db', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#6b7280' }}>Nenhum pedido de exame encontrado</Typography>
            <Typography variant="body2" sx={{ color: '#9ca3af', mb: 2 }}>Comece criando um novo pedido de exame</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { resetForm(); setDialogOpen(true); }}
              sx={{ bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none' }}>Novo Pedido</Button>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f0fdf4' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Número</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Paciente</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Profissional</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Exames</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Urgência</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Data</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((o: any) => (
                  <TableRow key={o.id} hover sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f0fdf4' } }}>
                    <TableCell sx={{ fontWeight: 600, color: '#0d9488' }}>{o.order_number}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{o.patient_name}</Typography>
                      {o.patient_cpf && <Typography variant="caption" sx={{ color: '#9ca3af' }}>{o.patient_cpf}</Typography>}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{o.professional_name || '-'}</Typography>
                    </TableCell>
                    <TableCell>{(o.items || []).length} exame(s)</TableCell>
                    <TableCell>
                      <Chip label={urgencyLabels[o.urgency] || o.urgency} size="small"
                        sx={{ bgcolor: `${urgencyColors[o.urgency]}20`, color: urgencyColors[o.urgency], fontWeight: 600, fontSize: '0.7rem' }} />
                    </TableCell>
                    <TableCell>{formatDate(o.issue_date)}</TableCell>
                    <TableCell>
                      <Chip label={statusLabels[o.status] || o.status} size="small"
                        sx={{ bgcolor: `${statusColors[o.status]}20`, color: statusColors[o.status], fontWeight: 600, fontSize: '0.7rem' }} />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Visualizar"><IconButton size="small" onClick={() => handleView(o)}><ViewIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Imprimir"><IconButton size="small" onClick={() => handlePrint(o)}><PrintIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Mais opções">
                        <IconButton size="small" onClick={(e) => { setAnchorEl(e.currentTarget); setSelectedOrder(o); }}><MoreIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* More options menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={() => { if (selectedOrder) handleEdit(selectedOrder); setAnchorEl(null); }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon><ListItemText>Editar</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { if (selectedOrder) handleDuplicate(selectedOrder.id); setAnchorEl(null); }}>
          <ListItemIcon><DuplicateIcon fontSize="small" /></ListItemIcon><ListItemText>Duplicar</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { if (selectedOrder) handlePrint(selectedOrder); setAnchorEl(null); }}>
          <ListItemIcon><PrintIcon fontSize="small" /></ListItemIcon><ListItemText>Imprimir</ListItemText>
        </MenuItem>
        {selectedOrder?.status === 'pending' && (
          <MenuItem onClick={() => { if (selectedOrder) handleStatusChange(selectedOrder.id, 'sent'); setAnchorEl(null); }}>
            <ListItemIcon><SendIcon fontSize="small" sx={{ color: '#3b82f6' }} /></ListItemIcon><ListItemText>Marcar como Enviado</ListItemText>
          </MenuItem>
        )}
        {selectedOrder?.status === 'sent' && (
          <MenuItem onClick={() => { if (selectedOrder) handleStatusChange(selectedOrder.id, 'completed'); setAnchorEl(null); }}>
            <ListItemIcon><CheckIcon fontSize="small" sx={{ color: '#10b981' }} /></ListItemIcon><ListItemText>Marcar como Concluído</ListItemText>
          </MenuItem>
        )}
        {selectedOrder?.status !== 'cancelled' && selectedOrder?.status !== 'completed' && (
          <MenuItem onClick={() => { if (selectedOrder) handleStatusChange(selectedOrder.id, 'cancelled'); setAnchorEl(null); }}>
            <ListItemIcon><CancelIcon fontSize="small" sx={{ color: '#f59e0b' }} /></ListItemIcon><ListItemText>Cancelar</ListItemText>
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => { if (selectedOrder) handleDelete(selectedOrder.id); setAnchorEl(null); }} sx={{ color: '#ef4444' }}>
          <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: '#ef4444' }} /></ListItemIcon><ListItemText>Excluir</ListItemText>
        </MenuItem>
      </Menu>

      {/* ====== CREATE/EDIT DIALOG ====== */}
      <Dialog open={dialogOpen} onClose={() => { setDialogOpen(false); resetForm(); }} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb', pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ScienceIcon sx={{ color: '#14b8a6' }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{editingId ? 'Editar Pedido de Exame' : 'Novo Pedido de Exame'}</Typography>
          </Box>
          <IconButton onClick={() => { setDialogOpen(false); resetForm(); }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 1, borderBottom: '1px solid #e5e7eb',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.8rem' },
            '& .Mui-selected': { color: '#14b8a6 !important' },
            '& .MuiTabs-indicator': { backgroundColor: '#14b8a6' },
          }}>
            <Tab label="Paciente & Profissional" />
            <Tab label="Exames" />
            <Tab label="Informações Clínicas" />
            <Tab label="Laboratório & Obs" />
          </Tabs>

          {/* TAB 0: PATIENT & PROFESSIONAL */}
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tipo de Pedido</InputLabel>
                  <Select value={orderForm.order_type} label="Tipo de Pedido"
                    onChange={(e) => setOrderForm({ ...orderForm, order_type: e.target.value })}>
                    {Object.entries(typeLabels).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Urgência</InputLabel>
                  <Select value={orderForm.urgency} label="Urgência"
                    onChange={(e) => setOrderForm({ ...orderForm, urgency: e.target.value })}>
                    {Object.entries(urgencyLabels).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: '#0d9488' }}>Paciente *</Typography>
            <Autocomplete
              options={patientOptions}
              getOptionLabel={(o: any) => `${o.full_name}${o.cpf ? ` - CPF: ${o.cpf}` : ''}${o.phone ? ` - Tel: ${o.phone}` : ''}`}
              value={selectedPatient}
              onChange={(_, v) => {
                setSelectedPatient(v);
                // Auto-fill allergies and medications from patient
                if (v) {
                  setOrderForm(f => ({
                    ...f,
                    allergies: (v.allergies || []).join(', ') || f.allergies,
                    current_medications: (v.medications || []).join(', ') || f.current_medications,
                  }));
                }
              }}
              onInputChange={(_, v) => setPatientSearch(v)}
              onOpen={() => { if (patientOptions.length === 0) setPatientSearch(''); }}
              filterOptions={(x) => x}
              renderInput={(params) => <TextField {...params} label="Buscar paciente" placeholder="Digite nome, CPF ou telefone..." fullWidth />}
              noOptionsText="Nenhum paciente encontrado"
              sx={{ mb: 3 }}
            />

            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: '#0d9488' }}>Profissional Solicitante</Typography>
            <Autocomplete
              options={professionalOptions}
              getOptionLabel={(o: any) => `${o.full_name}${o.registration_number ? ` - ${o.registration_type || 'CRM'} ${o.registration_number}` : ''}${o.specialty ? ` (${o.specialty})` : ''}`}
              value={selectedProfessional}
              onChange={(_, v) => setSelectedProfessional(v)}
              onInputChange={(_, v) => setProfessionalSearch(v)}
              onOpen={() => { if (professionalOptions.length === 0) setProfessionalSearch(''); }}
              filterOptions={(x) => x}
              renderInput={(params) => <TextField {...params} label="Buscar profissional" placeholder="Digite nome ou registro..." fullWidth />}
              noOptionsText="Nenhum profissional encontrado"
            />
          </TabPanel>

          {/* TAB 1: EXAMS */}
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0d9488' }}>Exames Solicitados</Typography>
              <Button size="small" startIcon={<AddItemIcon />} onClick={addItem}
                sx={{ color: '#14b8a6', textTransform: 'none', fontWeight: 600 }}>Adicionar Exame</Button>
            </Box>

            {items.map((item, index) => (
              <Card key={index} sx={{ mb: 2, p: 2, borderRadius: 2, border: '1px solid #e5e7eb', position: 'relative' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0d9488' }}>Exame {index + 1}</Typography>
                  {items.length > 1 && (
                    <IconButton size="small" onClick={() => removeItem(index)} sx={{ color: '#ef4444' }}><RemoveIcon /></IconButton>
                  )}
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Autocomplete
                      options={examOptions}
                      getOptionLabel={(o: any) => `${o.name}${o.code ? ` (${o.code})` : ''}${o.category?.name ? ` - ${o.category.name}` : ''}`}
                      onChange={(_, v) => selectExamForItem(index, v)}
                      onInputChange={(_, v) => setExamSearch(v)}
                      onOpen={() => { if (examOptions.length === 0) setExamSearch(''); }}
                      filterOptions={(x) => x}
                      renderInput={(params) => <TextField {...params} label="Buscar exame no catálogo" placeholder="Digite nome ou código..." size="small" fullWidth />}
                      noOptionsText={examSearch ? "Nenhum exame encontrado" : "Digite para buscar..."}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth size="small" label="Nome do Exame *" value={item.exam_name}
                      onChange={(e) => updateItem(index, 'exam_name', e.target.value)} />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField fullWidth size="small" label="Código" value={item.exam_code}
                      onChange={(e) => updateItem(index, 'exam_code', e.target.value)} />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField fullWidth size="small" label="Categoria" value={item.exam_category}
                      onChange={(e) => updateItem(index, 'exam_category', e.target.value)} />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField fullWidth size="small" type="number" inputProps={{ min: 0, step: 0.01 }} label="Valor (R$)" value={item.price ?? ''}
                      onChange={(e) => updateItem(index, 'price', e.target.value === '' ? '' : Number(e.target.value))} />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField fullWidth size="small" type="number" inputProps={{ min: 0, max: 100, step: 0.5 }} label="Comissão % (solicitante)" value={item.commission_percentage ?? ''}
                      placeholder="Vazio = sem comissão"
                      onChange={(e) => updateItem(index, 'commission_percentage', e.target.value === '' ? '' : Number(e.target.value))} />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField fullWidth size="small" label="Material/Amostra" value={item.specimen_type}
                      onChange={(e) => updateItem(index, 'specimen_type', e.target.value)} placeholder="Ex: Sangue" />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField fullWidth size="small" label="Região do Corpo" value={item.body_region}
                      onChange={(e) => updateItem(index, 'body_region', e.target.value)} placeholder="Ex: Abdômen" />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Lateralidade</InputLabel>
                      <Select value={item.laterality} label="Lateralidade"
                        onChange={(e) => updateItem(index, 'laterality', e.target.value)}>
                        {lateralityOptions.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Urgência</InputLabel>
                      <Select value={item.urgency} label="Urgência"
                        onChange={(e) => updateItem(index, 'urgency', e.target.value)}>
                        {Object.entries(urgencyLabels).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth size="small" label="Preparo do Paciente" value={item.preparation_instructions}
                      onChange={(e) => updateItem(index, 'preparation_instructions', e.target.value)} multiline rows={2}
                      placeholder="Ex: Jejum de 12 horas" />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth size="small" label="Justificativa Clínica" value={item.clinical_justification}
                      onChange={(e) => updateItem(index, 'clinical_justification', e.target.value)} multiline rows={2} />
                  </Grid>
                </Grid>
              </Card>
            ))}
          </TabPanel>

          {/* TAB 2: CLINICAL INFO */}
          <TabPanel value={tabValue} index={2}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth label="Indicação Clínica / Hipótese Diagnóstica *" value={orderForm.clinical_indication}
                  onChange={(e) => setOrderForm({ ...orderForm, clinical_indication: e.target.value })} multiline rows={2}
                  placeholder="Descreva a indicação clínica para a solicitação dos exames" />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField fullWidth label="Diagnóstico" value={orderForm.diagnosis}
                  onChange={(e) => setOrderForm({ ...orderForm, diagnosis: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="CID-10" value={orderForm.diagnosis_code}
                  onChange={(e) => setOrderForm({ ...orderForm, diagnosis_code: e.target.value })} placeholder="Ex: R10.4" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Resumo da História Clínica" value={orderForm.clinical_history}
                  onChange={(e) => setOrderForm({ ...orderForm, clinical_history: e.target.value })} multiline rows={2} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Medicamentos em Uso" value={orderForm.current_medications}
                  onChange={(e) => setOrderForm({ ...orderForm, current_medications: e.target.value })} multiline rows={2} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Alergias" value={orderForm.allergies}
                  onChange={(e) => setOrderForm({ ...orderForm, allergies: e.target.value })} multiline rows={2} />
              </Grid>
            </Grid>
          </TabPanel>

          {/* TAB 3: DESTINATION & NOTES */}
          <TabPanel value={tabValue} index={3}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: '#0d9488' }}>Laboratório / Clínica de Destino</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Nome do Laboratório" value={orderForm.destination_lab}
                  onChange={(e) => setOrderForm({ ...orderForm, destination_lab: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField fullWidth label="Telefone" value={orderForm.destination_phone}
                  onChange={(e) => setOrderForm({ ...orderForm, destination_phone: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField fullWidth label="Endereço" value={orderForm.destination_address}
                  onChange={(e) => setOrderForm({ ...orderForm, destination_address: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Instruções Especiais para o Paciente" value={orderForm.special_instructions}
                  onChange={(e) => setOrderForm({ ...orderForm, special_instructions: e.target.value })} multiline rows={2}
                  placeholder="Instruções que o paciente precisa seguir antes da coleta" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Observações (visíveis no pedido impresso)" value={orderForm.notes}
                  onChange={(e) => setOrderForm({ ...orderForm, notes: e.target.value })} multiline rows={3} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Notas Internas (NÃO aparecem no pedido impresso)" value={orderForm.internal_notes}
                  onChange={(e) => setOrderForm({ ...orderForm, internal_notes: e.target.value })} multiline rows={2}
                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fef3c7' } }} />
              </Grid>
            </Grid>
          </TabPanel>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #e5e7eb', gap: 1 }}>
          <Button onClick={() => { setDialogOpen(false); resetForm(); }} sx={{ color: '#6b7280', textTransform: 'none' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none', fontWeight: 600, minWidth: 160 }}>
            {saving ? <CircularProgress size={20} /> : editingId ? 'Salvar Alterações' : 'Criar Pedido'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ====== VIEW DIALOG ====== */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ScienceIcon sx={{ color: '#14b8a6' }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Pedido {viewOrder?.order_number}</Typography>
            {viewOrder && (
              <>
                <Chip label={urgencyLabels[viewOrder.urgency]} size="small" sx={{ bgcolor: `${urgencyColors[viewOrder.urgency]}20`, color: urgencyColors[viewOrder.urgency], fontWeight: 600 }} />
                <Chip label={statusLabels[viewOrder.status]} size="small" sx={{ bgcolor: `${statusColors[viewOrder.status]}20`, color: statusColors[viewOrder.status], fontWeight: 600 }} />
              </>
            )}
          </Box>
          <IconButton onClick={() => setViewDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {viewOrder && (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0d9488', mb: 1 }}>Paciente</Typography>
                  <Typography variant="body2"><strong>Nome:</strong> {viewOrder.patient_name}</Typography>
                  {viewOrder.patient_cpf && <Typography variant="body2"><strong>CPF:</strong> {viewOrder.patient_cpf}</Typography>}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0d9488', mb: 1 }}>Profissional</Typography>
                  <Typography variant="body2"><strong>Nome:</strong> {viewOrder.professional_name || '-'}</Typography>
                  {viewOrder.professional_registration && <Typography variant="body2"><strong>Registro:</strong> {viewOrder.professional_registration}</Typography>}
                </Grid>
              </Grid>

              {viewOrder.clinical_indication && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0d9488', mb: 1 }}>Indicação Clínica</Typography>
                  <Typography variant="body2">{viewOrder.clinical_indication}</Typography>
                </Box>
              )}

              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0d9488', mb: 1 }}>Exames Solicitados</Typography>
              <TableContainer sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f0fdf4' }}>
                      <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Exame</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Código</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Material</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Urgência</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(viewOrder.items || []).map((item: any, idx: number) => (
                      <TableRow key={item.id || idx}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.exam_name}</Typography>
                          {item.body_region && <Typography variant="caption" sx={{ color: '#9ca3af' }}>Região: {item.body_region}</Typography>}
                        </TableCell>
                        <TableCell>{item.exam_code || '-'}</TableCell>
                        <TableCell>{item.specimen_type || '-'}</TableCell>
                        <TableCell>
                          <Chip label={urgencyLabels[item.urgency] || 'Rotina'} size="small"
                            sx={{ bgcolor: `${urgencyColors[item.urgency] || '#14b8a6'}20`, color: urgencyColors[item.urgency] || '#14b8a6', fontSize: '0.65rem' }} />
                        </TableCell>
                        <TableCell>{statusLabels[item.status] || item.status || 'Pendente'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb', gap: 1 }}>
          <Button onClick={() => { if (viewOrder) handlePrint(viewOrder); }} startIcon={<PrintIcon />}
            sx={{ color: '#14b8a6', textTransform: 'none', fontWeight: 600 }}>Imprimir</Button>
          <Button onClick={() => { if (viewOrder) handleEdit(viewOrder); setViewDialogOpen(false); }} startIcon={<EditIcon />}
            sx={{ color: '#3b82f6', textTransform: 'none', fontWeight: 600 }}>Editar</Button>
          <Button onClick={() => setViewDialogOpen(false)} sx={{ textTransform: 'none' }}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* ====== PRINT DIALOG ====== */}
      <Dialog open={printDialogOpen} onClose={() => setPrintDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Pré-visualização do Pedido de Exames</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" startIcon={<PrintIcon />} onClick={executePrint}
              sx={{ bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none', fontWeight: 600 }}>Imprimir</Button>
            <IconButton onClick={() => setPrintDialogOpen(false)}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {printData && (
            <div ref={printRef}>
              <div className="print-container">
                {/* Header */}
                <div className="doc-head">
                  {printData.clinic?.logo_url && (
                    <div className="doc-head-logo"><img src={printData.clinic.logo_url} alt="" /></div>
                  )}
                  <div className="doc-head-center">
                    <div className="clinic-name">{printData.clinic?.clinic_name || 'Clínica'}</div>
                    {printData.clinic?.clinic_subtitle && <div className="clinic-subtitle">{printData.clinic.clinic_subtitle}</div>}
                  </div>
                  <div className="doc-head-meta">
                    {printData.clinic?.cnpj && <div>CNPJ: {printData.clinic.cnpj}</div>}
                    {printData.clinic?.address_street && (
                      <div>{printData.clinic.address_street}{printData.clinic.address_number ? `, ${printData.clinic.address_number}` : ''}{printData.clinic.address_neighborhood ? ` — ${printData.clinic.address_neighborhood}` : ''}</div>
                    )}
                    {(printData.clinic?.address_city || printData.clinic?.address_state) && (
                      <div>{[printData.clinic.address_city, printData.clinic.address_state].filter(Boolean).join(' - ')}</div>
                    )}
                    {(printData.clinic?.phone || printData.clinic?.whatsapp) && (
                      <div>Tel: {printData.clinic.phone || printData.clinic.whatsapp}</div>
                    )}
                    {printData.clinic?.email && <div>{printData.clinic.email}</div>}
                  </div>
                </div>

                {/* Title */}
                <div className="order-title">SOLICITAÇÃO DE EXAMES</div>
                {printData.order?.urgency !== 'routine' && (
                  <div className="urgency-badge">
                    <span style={{ backgroundColor: printData.order?.urgency === 'emergency' ? '#fef2f2' : '#fefce8', color: printData.order?.urgency === 'emergency' ? '#ef4444' : '#f59e0b' }}>
                      {urgencyLabels[printData.order?.urgency] || 'Rotina'}
                    </span>
                  </div>
                )}
                <div className="order-number">
                  Nº {printData.order?.order_number} | Emissão: {formatDate(printData.order?.issue_date)} | Validade: {formatDate(printData.order?.validity_date)}
                </div>

                {/* Patient info */}
                <div className="section">
                  <div className="section-title">Dados do Paciente</div>
                  <div className="info-grid">
                    <div className="info-item"><span className="info-label">Nome: </span><span className="info-value">{printData.order?.patient_name}</span></div>
                    {printData.order?.patient_cpf && <div className="info-item"><span className="info-label">CPF: </span><span className="info-value">{printData.order.patient_cpf}</span></div>}
                    {printData.order?.patient_age_text && <div className="info-item"><span className="info-label">Idade: </span><span className="info-value">{printData.order.patient_age_text}</span></div>}
                    {printData.order?.patient_gender && <div className="info-item"><span className="info-label">Sexo: </span><span className="info-value">{printData.order.patient_gender === 'masculine' ? 'Masculino' : printData.order.patient_gender === 'feminine' ? 'Feminino' : printData.order.patient_gender}</span></div>}
                    {printData.order?.patient_address && <div className="info-item"><span className="info-label">Endereço: </span><span className="info-value">{printData.order.patient_address}</span></div>}
                  </div>
                </div>

                {/* Clinical indication */}
                <div className="section">
                  <div className="section-title">Informações Clínicas</div>
                  {printData.order?.clinical_indication && <div className="info-item" style={{ marginBottom: '4px' }}><span className="info-label">Indicação Clínica: </span><span className="info-value">{printData.order.clinical_indication}</span></div>}
                  {printData.order?.diagnosis && <div className="info-item" style={{ marginBottom: '4px' }}><span className="info-label">Diagnóstico: </span><span className="info-value">{printData.order.diagnosis}{printData.order?.diagnosis_code ? ` (CID: ${printData.order.diagnosis_code})` : ''}</span></div>}
                  {printData.order?.clinical_history && <div className="info-item" style={{ marginBottom: '4px' }}><span className="info-label">História Clínica: </span><span className="info-value">{printData.order.clinical_history}</span></div>}
                  {printData.order?.current_medications && <div className="info-item" style={{ marginBottom: '4px' }}><span className="info-label">Medicamentos em Uso: </span><span className="info-value">{printData.order.current_medications}</span></div>}
                  {printData.order?.allergies && <div className="info-item"><span className="info-label">Alergias: </span><span className="info-value">{printData.order.allergies}</span></div>}
                </div>

                {/* Exams table */}
                <div className="section">
                  <div className="section-title">Exames Solicitados</div>
                  <table>
                    <thead>
                      <tr>
                        <th style={{ width: '5%' }}>#</th>
                        <th style={{ width: '10%' }}>Código</th>
                        <th>Exame</th>
                        <th style={{ width: '12%' }}>Material</th>
                        <th style={{ width: '12%' }}>Região</th>
                        <th style={{ width: '8%' }}>Urgência</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(printData.order?.items || []).map((item: any, idx: number) => (
                        <tr key={item.id || idx}>
                          <td>{idx + 1}</td>
                          <td>{item.exam_code || '-'}</td>
                          <td>
                            <span className="exam-name">{item.exam_name}</span>
                            {item.clinical_justification && <div className="exam-prep">Justificativa: {item.clinical_justification}</div>}
                          </td>
                          <td>{item.specimen_type || '-'}</td>
                          <td>{item.body_region || '-'}{item.laterality && item.laterality !== 'Não se aplica' ? ` (${item.laterality})` : ''}</td>
                          <td style={{ color: urgencyColors[item.urgency] || '#14b8a6', fontWeight: 600, fontSize: '10px' }}>
                            {urgencyLabels[item.urgency] || 'Rotina'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Preparation instructions */}
                {(printData.order?.items || []).some((i: any) => i.preparation_instructions) && (
                  <div className="preparation-box">
                    <h4>Orientações de Preparo ao Paciente</h4>
                    {(printData.order?.items || []).filter((i: any) => i.preparation_instructions).map((item: any, idx: number) => (
                      <div className="preparation-item" key={idx}>
                        <strong>{item.exam_name}:</strong> {item.preparation_instructions}
                      </div>
                    ))}
                  </div>
                )}

                {/* Special instructions */}
                {printData.order?.special_instructions && (
                  <div className="preparation-box" style={{ marginTop: '8px' }}>
                    <h4>Instruções Especiais</h4>
                    <div className="preparation-item">{printData.order.special_instructions}</div>
                  </div>
                )}

                {/* Destination lab */}
                {printData.order?.destination_lab && (
                  <div className="destination-box">
                    <div style={{ fontWeight: 600, fontSize: '11px', marginBottom: '4px' }}>Laboratório / Clínica de Destino</div>
                    <div style={{ fontSize: '10px' }}>{printData.order.destination_lab}</div>
                    {printData.order.destination_phone && <div style={{ fontSize: '10px' }}>Tel: {printData.order.destination_phone}</div>}
                    {printData.order.destination_address && <div style={{ fontSize: '10px' }}>{printData.order.destination_address}</div>}
                  </div>
                )}

                {/* Notes */}
                {printData.order?.notes && (
                  <div className="section" style={{ marginTop: '12px' }}>
                    <div className="section-title">Observações</div>
                    <p style={{ fontSize: '11px', lineHeight: '1.6' }}>{printData.order.notes}</p>
                  </div>
                )}

                {/* Validity */}
                <div className="validity">
                  Pedido válido até {formatDate(printData.order?.validity_date)}
                </div>

                {/* Signatures */}
                <div className="clinic-doc-sign-zone">
                  <div className="clinic-doc-sign-row">
                    <div className="clinic-doc-sign-slot">
                      {(() => {
                        const u = String(printData.order?.professional_signature_image_url ?? '').trim();
                        return (
                          <>
                            {u ? (
                              <div className="clinic-doc-sign-prof-img-wrap">
                                <img src={u} alt="" />
                              </div>
                            ) : null}
                            <div className={u ? 'clinic-doc-sign-spacer clinic-doc-sign-spacer--with-img' : 'clinic-doc-sign-spacer'} />
                            <div className="clinic-doc-sign-rule" />
                            <p className="clinic-doc-sign-caption">{printData.order?.professional_name || printData.clinic?.responsible_name || 'Profissional Responsável'}</p>
                            {(printData.order?.professional_registration || printData.clinic?.responsible_registration) && (
                              <p className="clinic-doc-sign-caption">
                                {printData.order?.professional_registration_type || printData.clinic?.responsible_registration_type || 'CRM'}{' '}
                                {printData.order?.professional_registration || printData.clinic?.responsible_registration}
                                {printData.order?.professional_registration_state ? ` - ${printData.order.professional_registration_state}` : ''}
                              </p>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    <div className="clinic-doc-sign-slot clinic-doc-sign-slot--narrow">
                      <div className="clinic-doc-sign-spacer" />
                      <div className="clinic-doc-sign-rule" />
                      <p className="clinic-doc-sign-caption">Data: ___/___/______</p>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                {printData.clinic?.exam_order_footer_text && (
                  <div className="footer-note">{printData.clinic.exam_order_footer_text}</div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Catalog */}
      <Dialog open={catalogDialogOpen} onClose={() => setCatalogDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Catálogo de Exames</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" startIcon={<AddIcon />} onClick={() => { setCatalogForm({ name: '', code: '', category_id: '', price: '', commission_percentage: '', description: '' }); setCatalogEditingId(null); setCatalogFormOpen(true); }} sx={{ color: '#14b8a6' }}>Novo exame</Button>
            <IconButton onClick={() => setCatalogDialogOpen(false)}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {catalogLoading ? <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box> : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Nome</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Categoria</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Valor</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Comissão %</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {catalogList.length === 0 ? <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3 }}>Nenhum exame. Clique em Novo exame.</TableCell></TableRow> : catalogList.map((row: any) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.code || '-'}</TableCell>
                      <TableCell>{row.category?.name || '-'}</TableCell>
                      <TableCell align="right">{row.price != null ? Number(row.price).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : '-'}</TableCell>
                      <TableCell align="right">{row.commission_percentage != null ? `${Number(row.commission_percentage)}%` : '-'}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => { setCatalogForm({ name: row.name || '', code: row.code || '', category_id: row.category_id || '', price: row.price ?? '', commission_percentage: row.commission_percentage ?? '', description: row.description || '' }); setCatalogEditingId(row.id); setCatalogFormOpen(true); }}><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" onClick={async () => { if (!window.confirm('Excluir?')) return; try { await axios.delete(getApiUrl(`/clinic/exam-orders/catalog/${row.id}`)); setCatalogList(prev => prev.filter((x: any) => x.id !== row.id)); setSnackbar({ open: true, message: 'Removido', severity: 'success' }); } catch (e: any) { setSnackbar({ open: true, message: e.response?.data?.error || 'Erro', severity: 'error' }); } }} sx={{ color: '#ef4444' }}><DeleteIcon fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={catalogFormOpen} onClose={() => setCatalogFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{catalogEditingId ? 'Editar exame' : 'Novo exame'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}><TextField fullWidth size="small" label="Nome *" value={catalogForm.name} onChange={(e) => setCatalogForm(f => ({ ...f, name: e.target.value }))} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Código" value={catalogForm.code} onChange={(e) => setCatalogForm(f => ({ ...f, code: e.target.value }))} /></Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Categoria</InputLabel>
                <Select value={catalogForm.category_id} label="Categoria" onChange={(e) => setCatalogForm(f => ({ ...f, category_id: e.target.value }))}>
                  <MenuItem value="">Nenhuma</MenuItem>
                  {catalogCategories.map((c: any) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" type="number" inputProps={{ min: 0, step: 0.01 }} label="Valor (R$)" value={catalogForm.price} onChange={(e) => setCatalogForm(f => ({ ...f, price: e.target.value }))} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" type="number" inputProps={{ min: 0, max: 100, step: 0.5 }} label="Comissão %" value={catalogForm.commission_percentage} onChange={(e) => setCatalogForm(f => ({ ...f, commission_percentage: e.target.value }))} placeholder="Vazio = sem comissão" /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Descrição" value={catalogForm.description} onChange={(e) => setCatalogForm(f => ({ ...f, description: e.target.value }))} multiline rows={2} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCatalogFormOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={async () => {
            if (!catalogForm.name.trim()) { setSnackbar({ open: true, message: 'Nome é obrigatório', severity: 'error' }); return; }
            try {
              const payload: any = { name: catalogForm.name.trim(), code: catalogForm.code || null, category_id: catalogForm.category_id || null, description: catalogForm.description || null };
              if (catalogForm.price !== '') payload.price = Number(catalogForm.price);
              if (catalogForm.commission_percentage !== '') payload.commission_percentage = Number(catalogForm.commission_percentage);
              if (catalogEditingId) {
                await axios.put(getApiUrl(`/clinic/exam-orders/catalog/${catalogEditingId}`), payload);
                setCatalogList(prev => prev.map((x: any) => x.id === catalogEditingId ? { ...x, ...payload } : x));
                setSnackbar({ open: true, message: 'Exame atualizado', severity: 'success' });
              } else {
                const res = await axios.post(getApiUrl('/clinic/exam-orders/catalog'), payload);
                setCatalogList(prev => [...prev, res.data.data]);
                setSnackbar({ open: true, message: 'Exame cadastrado', severity: 'success' });
              }
              setCatalogFormOpen(false);
            } catch (e: any) { setSnackbar({ open: true, message: e.response?.data?.error || 'Erro', severity: 'error' }); }
          }} sx={{ bgcolor: '#14b8a6' }}>{catalogEditingId ? 'Salvar' : 'Cadastrar'}</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ExamOrders;
